import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { complaintAPI, customerAPI, userAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Plus, Search, MessageSquare, AlertCircle, X, ImagePlus, Film, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useViewMode } from '../hooks/useViewMode';
import { AppSheet } from '../components/common/AppViewKit';
import useDarkReady from '../theme/useDarkReady';
import Num from '../components/bits/Num';

const PRIORITY_BADGE = { low: 'info', medium: 'pending', high: 'warning', critical: 'danger' };
const STATUS_BADGE = { open: 'open', in_progress: 'in_progress', resolved: 'resolved', closed: 'closed' };

// Title/Description default to the picked category's label — staff can still type over
// either manually. Re-picking a category only overwrites a field that's still exactly the
// PREVIOUS category's auto-filled label (untouched by the user), never a manual edit.
const CATEGORY_LABELS = {
  no_internet: 'No Internet',
  slow_internet: 'Slow Internet',
  red_light_router: 'Red Light in Router',
  cable_cut: 'Cable Cut',
  router_issue: 'Router Issue',
  e61_no_signal: 'E61 - No Signal',
  e51_channel_error: 'E51/999 Channel Error',
  channel_stuck: 'ચેનલ ચોટે છે',
  colors_only: 'Colors Only',
  relocation_transfer: 'Relocation/Transfer',
  billing_dispute: 'Billing Dispute',
  device_issue: 'Device Issue',
  installation_request: 'Installation Request',
  other: 'Other',
};

function NewComplaintModal({ onClose, onSuccess }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer: '', category: 'no_internet', title: CATEGORY_LABELS.no_internet, description: CATEGORY_LABELS.no_internet,
    priority: 'medium', serviceType: 'broadband', sendWhatsapp: true, assignedTo: ''
  });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]); // [{file, preview, isVideo}] — up to 5, matches backend's complaintMediaUpload limit
  const [staff, setStaff] = useState([]);

  // rank=1: best matches first (exact house number before "contains"), across CATV + broadband.
  // status=active: inactive/suspended customers can't raise a ticket from here.
  // `stale` drops a slow earlier response so it can't overwrite a newer one while typing.
  useEffect(() => {
    let stale = false;
    if (search.trim().length >= 2) {
      customerAPI.getAll({ search: search.trim(), limit: 20, rank: 1, status: 'active' })
        .then(({ data }) => { if (!stale) setCustomers(data.data); })
        .catch(() => {});
    } else {
      setCustomers([]);
    }
    return () => { stale = true; };
  }, [search]);

  // Same "everyone, labeled with role" list ComplaintDetailPage's post-creation Assign
  // dropdown uses — lets the creator hand a ticket straight to whoever should work it
  // instead of always leaving assignment for that separate step.
  useEffect(() => {
    userAPI.getAll().then(({ data }) => setStaff(data.data)).catch(() => {});
  }, []);

  const addImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - images.length);
    if (!files.length) return;
    setImages(list => [...list, ...files.map(file => ({ file, preview: URL.createObjectURL(file), isVideo: file.type.startsWith('video/') }))]);
    e.target.value = '';
  };
  const removeImage = (i) => setImages(list => list.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // assignedTo is an ObjectId ref on the backend — an empty string would fail to cast,
      // so "— Unassigned —" must omit the field entirely rather than send ''.
      const { assignedTo, ...rest } = form;
      const fields = assignedTo ? form : rest;
      let payload = fields;
      if (images.length) {
        payload = new FormData();
        Object.entries(fields).forEach(([k, v]) => payload.append(k, v));
        images.forEach(img => payload.append('media', img.file));
      }
      const { data } = await complaintAPI.create(payload);
      toast.success(`Ticket ${data.data.ticketNumber} created`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>New Support Ticket</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ background: 'var(--cb-eff6ff)', border: '1px solid var(--cb-bfdbfe)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: '.78rem', color: 'var(--c-1e3a8a)' }}>
              Disconnection, reconnection, package/plan change, or relocation? Use <strong>Service Requests</strong> instead — this form is for faults/issues.
            </div>
            <div className="form-group">
              <label className="form-label">Customer <span className="required">*</span></label>
              <input className="form-control" placeholder="Search by name, society, house no. (e.g. surbhi 6) or mobile..."
                value={search} onChange={e => setSearch(e.target.value)} />
              {customers.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4, maxHeight: 260, overflowY: 'auto' }}>
                  {customers.map(c => (
                    <div key={c._id} onClick={() => { setForm(f => ({ ...f, customer: c._id })); setSearch(`${c.name} (${c.customerId})`); setCustomers([]); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '.85rem', borderBottom: '1px solid var(--border)' }}
                      onMouseOver={e => e.currentTarget.style.background = 'var(--bg)'}
                      onMouseOut={e => e.currentTarget.style.background = 'white'}>
                      <div><strong>{c.name}</strong> · {c.customerId} · {c.mobile}</div>
                      {(c.address?.area || c.broadband?.houseNumber || c.catv?.houseNumber) && (
                        <div style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>
                          {[c.address?.area, c.broadband?.houseNumber || c.catv?.houseNumber].filter(Boolean).join(' - ')}{c.customerType ? ` · ${c.customerType}` : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-control" value={form.category} onChange={e => {
                  const category = e.target.value;
                  const newLabel = CATEGORY_LABELS[category] || '';
                  setForm(f => ({
                    ...f, category,
                    title: (!f.title || f.title === CATEGORY_LABELS[f.category]) ? newLabel : f.title,
                    description: (!f.description || f.description === CATEGORY_LABELS[f.category]) ? newLabel : f.description,
                  }));
                }}>
                  <option value="no_internet">No Internet</option>
                  <option value="slow_internet">Slow Internet</option>
                  <option value="red_light_router">Red Light in Router</option>
                  <option value="cable_cut">Cable Cut</option>
                  <option value="router_issue">Router Issue</option>
                  <option value="e61_no_signal">E61 - No Signal</option>
                  <option value="e51_channel_error">E51/999 Channel Error</option>
                  <option value="channel_stuck">ચેનલ ચોટે છે</option>
                  <option value="colors_only">Colors Only</option>
                  <option value="relocation_transfer">Relocation/Transfer</option>
                  <option value="billing_dispute">Billing Dispute</option>
                  <option value="device_issue">Device Issue</option>
                  <option value="installation_request">Installation Request</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select className="form-control" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Assign To (optional)</label>
              <select className="form-control" value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))}>
                <option value="">— Unassigned, assign later —</option>
                {staff.map(s => <option key={s._id} value={s._id}>{s.name} ({s.role})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Title <span className="required">*</span></label>
              <input className="form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Brief description of the issue" required />
            </div>
            <div className="form-group">
              <label className="form-label">Description <span className="required">*</span></label>
              <textarea className="form-control" rows={4} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Detailed description..." required />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.sendWhatsapp}
                  onChange={e => setForm(f => ({ ...f, sendWhatsapp: e.target.checked }))} />
                Send WhatsApp update to customer
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Photos / Videos (optional)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {images.map((img, i) => (
                  <div key={i} style={{ position: 'relative', width: 64, height: 64 }}>
                    {img.isVideo ? (
                      <>
                        <video src={img.preview} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, background: 'var(--cb-000000)' }} />
                        <Film size={16} color="var(--c-ffffff)" style={{ position: 'absolute', bottom: 4, left: 4, filter: 'drop-shadow(0 0 2px rgba(0,0,0,.8))' }} />
                      </>
                    ) : (
                      <img src={img.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                    )}
                    <button type="button" onClick={() => removeImage(i)}
                      style={{ position: 'absolute', top: -6, right: -6, background: 'rgba(0,0,0,.6)', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      <X size={12} color="var(--c-ffffff)" />
                    </button>
                  </div>
                ))}
                {images.length < 5 && (
                  <label style={{ width: 64, height: 64, border: '1px dashed var(--border)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
                    <ImagePlus size={20} />
                    <input type="file" accept="image/*,video/*" multiple hidden onChange={addImages} />
                  </label>
                )}
              </div>
              <p style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 6 }}>Up to 5 photos or videos of the issue.</p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !form.customer}>
              <MessageSquare size={14} /> {loading ? 'Creating...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const PRIORITY_COLOR = { low: 'var(--text-muted)', medium: 'var(--c-d97706)', high: 'var(--c-ea580c, #ea580c)', critical: 'var(--c-dc2626)' };
const EMPTY_FILTERS = { page: 1, status: '', priority: '', category: '', assignedTo: '', search: '' };

// Phone list: status tabs + search on top, one compact row per ticket, "+ New" floats above the shell's tab bar.
function ComplaintsApp({ filters, setFilters, stats, complaints, pagination, loading, staff, onNew, onOpen }) {
  const [sheet, setSheet] = useState(false);
  const set = (patch) => setFilters(f => ({ ...f, ...patch, page: 1 }));
  const tabs = [
    ['open', 'Open', stats?.open], ['in_progress', 'In progress', stats?.inProgress],
    ['resolved', 'Resolved', stats?.resolved], ['closed', 'Closed', stats?.closed], ['', 'All', stats?.total],
  ];
  const extra = [filters.priority, filters.category, filters.assignedTo].filter(Boolean).length;
  const chip = (on, label, onClick) => <button key={label} type="button" className={`mc-chip${on ? ' on' : ''}`} onClick={onClick}>{label}</button>;
  const lbl = { fontSize: '.74rem', fontWeight: 700, color: 'var(--text-muted)', margin: '12px 0 6px' };

  return (
    <div>
      <div style={{ position: 'sticky', top: -14, zIndex: 5, margin: '-14px -14px 8px', padding: '8px 14px', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
        <div className="mc-chips" style={{ marginBottom: 8 }}>
          {tabs.map(([v, l, n]) => chip(filters.status === v, n != null ? `${l} ${n}` : l, () => set({ status: v })))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="search-input-wrapper" style={{ flex: 1 }}>
            <Search size={14} className="search-icon" />
            <input className="search-input" style={{ width: '100%', minHeight: 40 }} placeholder="Ticket, name, mobile…"
              value={filters.search} onChange={e => set({ search: e.target.value })} />
          </div>
          <button type="button" className={`mc-chip${extra ? ' on' : ''}`} style={{ minHeight: 40, display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setSheet(true)}>
            <SlidersHorizontal size={15} /> {extra ? extra : 'Filter'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : complaints.length === 0 ? (
        <div className="empty-state"><MessageSquare size={32} /><h3>No tickets found</h3></div>
      ) : complaints.map(c => {
        const place = [c.customer?.address?.area, c.customer?.broadband?.houseNumber || c.customer?.catv?.houseNumber].filter(Boolean).join(' - ');
        return (
          <div key={c._id} onClick={() => onOpen(c._id)} role="button"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: `4px solid ${PRIORITY_COLOR[c.priority] || 'var(--border)'}`, borderRadius: 10, padding: '9px 12px', marginBottom: 8, cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: '.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.customer?.name || '—'}</span>
              <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>{format(new Date(c.createdAt), 'dd MMM, HH:mm')}</span>
            </div>
            <div style={{ fontSize: '.8rem', marginTop: 2 }}>{CATEGORY_LABELS[c.category] || c.category?.replace(/_/g, ' ')}{place ? <span style={{ color: 'var(--text-muted)' }}> · {place}</span> : null}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span className={`badge badge-${STATUS_BADGE[c.status]}`} style={{ textTransform: 'capitalize' }}>{c.status?.replace(/_/g, ' ')}</span>
              <span className={`badge badge-${PRIORITY_BADGE[c.priority]}`} style={{ textTransform: 'capitalize' }}>{c.priority}</span>
              <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{c.ticketNumber} · {c.assignedTo?.name || 'Unassigned'}</span>
            </div>
          </div>
        );
      })}

      {pagination.pages > 1 && (
        <div className="pagination">
          <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
          <span style={{ fontSize: '.8rem', padding: '0 8px' }}>{pagination.page} / {pagination.pages}</span>
          <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
        </div>
      )}

      <div style={{ position: 'sticky', bottom: 14, height: 0, display: 'flex', justifyContent: 'flex-end', pointerEvents: 'none' }}>
        <button type="button" className="btn btn-primary" onClick={onNew}
          style={{ transform: 'translateY(-100%)', pointerEvents: 'auto', borderRadius: 999, minHeight: 48, padding: '0 18px', boxShadow: '0 4px 14px rgba(0,0,0,.25)' }}>
          <Plus size={16} /> New
        </button>
      </div>

      {sheet && (
        <AppSheet title="Filter tickets" onClose={() => setSheet(false)}>
          <div style={{ ...lbl, marginTop: 0 }}>Priority</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[['', 'Any'], ['critical', 'Critical'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']].map(([v, l]) => chip(filters.priority === v, l, () => set({ priority: v })))}
          </div>
          <div style={lbl}>Technician</div>
          <select className="form-control" value={filters.assignedTo} onChange={e => set({ assignedTo: e.target.value })}>
            <option value="">Anyone</option>
            {staff.map(s => <option key={s._id} value={s._id}>{s.name} ({s.role})</option>)}
          </select>
          <div style={lbl}>Issue type</div>
          <select className="form-control" value={filters.category} onChange={e => set({ category: e.target.value })}>
            <option value="">All types</option>
            {Object.entries(CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => set({ priority: '', category: '', assignedTo: '' })}>Clear</button>
            <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={() => setSheet(false)}>Done</button>
          </div>
        </AppSheet>
      )}
    </div>
  );
}

export default function ComplaintsPage() {
  useDarkReady();
  const { user } = useAuth();
  const { viewMode } = useViewMode();
  const isApp = viewMode === 'app' || window.innerWidth < 640;
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [staff, setStaff] = useState([]);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, status: isApp ? 'open' : '' });
  const navigate = useNavigate();

  useEffect(() => {
    if (isApp) userAPI.getAll().then(({ data }) => setStaff(data.data)).catch(() => {});
  }, [isApp]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 20 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const [cd, sd] = await Promise.all([
        complaintAPI.getAll(params),
        complaintAPI.getStats()
      ]);
      setComplaints(cd.data.data);
      setPagination(cd.data.pagination);
      setStats(sd.data.data);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  if (isApp) {
    return (
      <>
        <ComplaintsApp filters={filters} setFilters={setFilters} stats={stats} complaints={complaints} pagination={pagination}
          loading={loading} staff={staff} onNew={() => setShowModal(true)} onOpen={id => navigate(`/complaints/${id}`)} />
        {showModal && <NewComplaintModal onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); load(); }} />}
      </>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Support Tickets</h2>
          <p className="text-muted text-small">{pagination.total || 0} total tickets</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={15} /> New Ticket
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-grid mb-6" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {[
            { label: 'Open', value: stats.open, color: 'var(--c-dc2626)', bg: 'var(--cb-fee2e2)' },
            { label: 'In Progress', value: stats.inProgress, color: 'var(--c-d97706)', bg: 'var(--cb-fef3c7)' },
            { label: 'SLA Breach', value: stats.overdueSLA, color: 'var(--primary)', bg: 'var(--cb-dbeafe)' },
            { label: 'Resolved', value: stats.resolved, color: 'var(--c-059669)', bg: 'var(--cb-d1fae5)' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-icon" style={{ background: s.bg }}>
                <AlertCircle size={20} color={s.color} />
              </div>
              <div className="stat-info"><p>{s.label}</p><h3><Num value={s.value} /></h3></div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search ticket, customer, mobile..."
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        </div>
        <select className="form-control" style={{ width: 140 }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select className="form-control" style={{ width: 130 }} value={filters.priority}
          onChange={e => setFilters(f => ({ ...f, priority: e.target.value, page: 1 }))}>
          <option value="">All Priority</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select className="form-control" style={{ width: 170 }} value={filters.category}
          onChange={e => setFilters(f => ({ ...f, category: e.target.value, page: 1 }))}>
          <option value="">All Categories</option>
          <option value="no_internet">No Internet</option>
          <option value="slow_internet">Slow Internet</option>
          <option value="e61_no_signal">E61 - No Signal</option>
          <option value="e51_channel_error">E51/999 Channel Error</option>
          <option value="billing_dispute">Billing</option>
          <option value="device_issue">Device Issue</option>
        </select>
        <button className="btn btn-secondary" onClick={() => setFilters(EMPTY_FILTERS)}>
          Clear
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : complaints.length === 0 ? (
            <div className="empty-state"><MessageSquare size={32} /><h3>No tickets found</h3></div>
          ) : (
            <table>
              <thead>
                <tr><th>Ticket</th><th>Customer</th><th>Category</th><th>Priority</th><th>Status</th><th>Assigned</th><th>Created</th><th></th></tr>
              </thead>
              <tbody>
                {complaints.map(c => (
                  <tr key={c._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/complaints/${c._id}`)}>
                    <td className="font-mono" style={{ fontSize: '.78rem' }}>{c.ticketNumber}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.customer?.name}</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{c.customer?.mobile}</div>
                      {(c.customer?.address?.area || c.customer?.broadband?.houseNumber || c.customer?.catv?.houseNumber) && (
                        <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                          {[c.customer?.address?.area, c.customer?.broadband?.houseNumber || c.customer?.catv?.houseNumber].filter(Boolean).join(' - ')}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '.82rem', textTransform: 'capitalize' }}>{c.category?.replace(/_/g, ' ')}</td>
                    <td>
                      <span className={`badge badge-${PRIORITY_BADGE[c.priority]}`} style={{ textTransform: 'capitalize' }}>
                        {c.priority}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${STATUS_BADGE[c.status]}`} style={{ textTransform: 'capitalize' }}>
                        {c.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ fontSize: '.82rem' }}>{c.assignedTo?.name || <span style={{ color: 'var(--text-light)' }}>Unassigned</span>}</td>
                    <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{format(new Date(c.createdAt), 'dd/MM/yy HH:mm')}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <Link to={`/complaints/${c._id}`} className="btn btn-secondary btn-sm">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="pagination">
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <span style={{ fontSize: '.8rem', padding: '0 8px' }}>{pagination.page} / {pagination.pages}</span>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>

      {showModal && <NewComplaintModal onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); load(); }} />}
    </div>
  );
}
